import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { labelBase, borderBase, inputBase } from '../components/shared/helpers';

export default function Login() {
  const { fullName, setFullName, phone, setPhone, email, setEmail, fading, go } = useBooking();
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Counts failed submit attempts — keys the failing field wrappers so the
  // shake replays on every failed attempt, not just the first.
  const [attempt, setAttempt] = useState(0);

  // Failing field: red bottom border + shake. The shake is motion-safe only;
  // reduced-motion users still get the red border and message.
  const errorBorder = 'border-b border-[#E5484D] pb-2.5 transition-colors duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] motion-safe:animate-shake';

  const handleSendOtp = async () => {
    const next = {};
    if (!fullName.trim()) next.fullName = 'Please enter your full name';
    if (!/^[6-9]\d{9}$/.test(phone.trim())) next.phone = 'Enter a valid 10-digit mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Please enter a valid email address';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setAttempt(a => a + 1);
      return;
    }

    setSubmitError('');
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('otp', {
        body: { action: 'send', fullName: fullName.trim(), phone: phone.trim(), email: email.trim() },
      });
      if (error || !data?.success) {
        setSubmitError(data?.error || 'Could not send the code. Please try again.');
        return;
      }
      // Funnel step 1 — OTP requested (no PII: the phone/email stay in context).
      trackEvent('login_started', { method: 'otp' });
      // Customer details are already held in context via the controlled inputs
      go('/verify');
    } catch (err) {
      setSubmitError('Could not send the code. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
          Begin your voyage
        </h1>
        <p className="font-dm-sans text-sm text-white/55 font-light mb-7 leading-relaxed">
          Enter your details to continue
        </p>

        <div className="flex flex-col gap-5">

          <div className="group flex flex-col gap-2">
            <label className={labelBase}>Full Name</label>
            <div className={borderBase}>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value.replace(/\d/g, ''))} className={inputBase} autoComplete="name" />
            </div>
            {errors.fullName && (
              <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed">{errors.fullName}</p>
            )}
          </div>

          <div className="group flex flex-col gap-2">
            <label className={labelBase}>Mobile Number</label>
            <div key={errors.phone ? `phone-err-${attempt}` : 'phone'} className={`flex items-end gap-3 ${errors.phone ? errorBorder : borderBase}`}>
              <span className="font-dm-sans text-[15px] font-light text-[rgba(201,168,76,0.75)] shrink-0 leading-none">+91</span>
              <div className="w-px h-3.5 bg-white/20 self-end mb-0.5 shrink-0" />
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="flex-1 bg-transparent text-white font-dm-sans text-[15px] font-light outline-none caret-[#C9A84C]" autoComplete="tel" />
            </div>
            {errors.phone ? (
              <p className="font-dm-sans text-sm text-[#E5484D] font-light leading-relaxed">{errors.phone}</p>
            ) : (
              <p className="font-dm-sans text-[13px] text-white/40 font-light leading-relaxed">Your OTP and ticket will be sent here.</p>
            )}
          </div>

          <div className="group flex flex-col gap-2">
            <label className={labelBase}>Email</label>
            <div key={errors.email ? `email-err-${attempt}` : 'email'} className={errors.email ? errorBorder : borderBase}>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputBase} autoComplete="email" />
            </div>
            {errors.email && (
              <p className="font-dm-sans text-sm text-[#E5484D] font-light leading-relaxed">{errors.email}</p>
            )}
          </div>

        </div>

        <button
          type="button"
          onClick={handleSendOtp}
          disabled={sending}
          className="w-full mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
        >
          {sending ? 'Sending...' : 'Send OTP'}
        </button>

        {submitError && (
          <p className="font-dm-sans text-center text-sm text-white/55 mt-3.5 leading-relaxed">
            {submitError}
          </p>
        )}

        <p className="font-dm-sans text-center text-sm text-white/55 mt-3.5 leading-relaxed">
          Your code arrives by WhatsApp and SMS. Your ticket follows on WhatsApp.
        </p>

        <div className="mt-6 pt-4 border-t border-white/[0.07] text-center">
          <Link
            to="/partner/login"
            className="font-dm-sans text-[13px] text-white/40 hover:text-[rgba(201,168,76,0.85)] transition-colors duration-300"
          >
            Travel partner? Sign in here
          </Link>
        </div>

      </div>
    </div>
  );
}
